import type { RootState } from '@/app/store'
import type { PageConfigRequest } from '@/types/api/page-config'
import type { OrderStatusGroup } from '@/types/enums/trade-desk-trade-blotter'
import type { TraderViewStatus } from '@/types/enums/trader-view-status'

import {
  createAsyncThunk,
  createSlice,
  type PayloadAction,
} from '@reduxjs/toolkit'

import { updatePageConfig } from '@/legacy-api/page-config-slice'
import { defaultPageConfigs } from '@/reducers/defaults/page-configs'
import { Page } from '@/types/schema/page-configs'
import {
  MOMENT_MARKETS_TRADE_BLOTTER_SCHEMA_VERSION,
  type TradeDeskTradeBlotterConfig,
} from '@/types/schema/page-configs/trade-desk-trade-blotter'

interface TradeDeskTradeBlotterState {
  config: TradeDeskTradeBlotterConfig
  configVersion: string
  defaultConfig: TradeDeskTradeBlotterConfig
  orderStatusGroup: OrderStatusGroup | null
  searchTerm: string | null
  selectedOrder: string | null
  activeTabQuickFilter: OrderStatusGroup | null
  completedTabQuickFilter: TraderViewStatus | null
  assignedToFilter: string | null
}

const initialState: TradeDeskTradeBlotterState = {
  config: defaultPageConfigs[Page.TradeDeskTradeBlotter],
  configVersion: MOMENT_MARKETS_TRADE_BLOTTER_SCHEMA_VERSION,
  defaultConfig: defaultPageConfigs[Page.TradeDeskTradeBlotter],
  orderStatusGroup: null,
  searchTerm: null,
  selectedOrder: null,
  activeTabQuickFilter: null,
  completedTabQuickFilter: null,
  assignedToFilter: null,
}

const tradeDeskTradeBlotterSlice = createSlice({
  name: 'tradeDeskTradeBlotter',
  reducerPath: 'tradeDeskTradeBlotter',
  initialState,
  reducers: {
    initializeTradeDeskTradeBlotterConfig:
      handleInitializeTradeDeskTradeBlotterConfig,
    reset: handleReset,
    resetOrderStatusGroup: handleResetOrderStatusGroup,
    resetSearchTerm: handleResetSearchTerm,
    resetTradeDeskTradeBlotterConfig: handleResetTradeDeskTradeBlotterConfig,
    setOrderStatusGroup: handleSetOrderStatusGroup,
    setSearchTerm: handleSetSearchTerm,
    setTradeDeskTradeBlotterConfig: handleSetTradeDeskTradeBlotterConfig,
    setActiveTabQuickFilter: handleSetActiveTabQuickFilter,
    setCompletedTabQuickFilter: handleSetCompletedTabQuickFilter,
    setOrderAssignedToQuickFilter: handleSetOrderAssignedToQuickFilter,
  },
})

function handleReset(state: TradeDeskTradeBlotterState) {
  Object.assign(state, initialState)
}

function handleInitializeTradeDeskTradeBlotterConfig(
  state: TradeDeskTradeBlotterState,
  action: PayloadAction<{
    config: TradeDeskTradeBlotterConfig
    version: string
  }>,
) {
  state.config = action.payload.config
  state.configVersion = action.payload.version
  // Override default config and version with the ones passed in from the API
  state.defaultConfig = action.payload.config
}

function handleResetTradeDeskTradeBlotterConfig(
  state: TradeDeskTradeBlotterState,
) {
  state.config = state.defaultConfig
}

function handleSetTradeDeskTradeBlotterConfig(
  state: TradeDeskTradeBlotterState,
  action: PayloadAction<Partial<TradeDeskTradeBlotterConfig>>,
) {
  const updatedConfig = {
    ...state.config,
    ...action.payload,
  }
  state.config = updatedConfig
}

function handleResetOrderStatusGroup(state: TradeDeskTradeBlotterState) {
  state.orderStatusGroup = null
}

function handleSetOrderStatusGroup(
  state: TradeDeskTradeBlotterState,
  action: PayloadAction<OrderStatusGroup>,
) {
  state.orderStatusGroup = action.payload
}

function handleSetSearchTerm(
  state: TradeDeskTradeBlotterState,
  action: PayloadAction<string>,
) {
  state.searchTerm = action.payload
}

function handleResetSearchTerm(state: TradeDeskTradeBlotterState) {
  state.searchTerm = null
}

function handleSetActiveTabQuickFilter(
  state: TradeDeskTradeBlotterState,
  action: PayloadAction<OrderStatusGroup>,
) {
  state.activeTabQuickFilter = action.payload
}

function handleSetCompletedTabQuickFilter(
  state: TradeDeskTradeBlotterState,
  action: PayloadAction<TraderViewStatus>,
) {
  state.completedTabQuickFilter = action.payload
}

function handleSetOrderAssignedToQuickFilter(
  state: TradeDeskTradeBlotterState,
  action: PayloadAction<string | null>,
) {
  if (typeof action.payload !== 'string') {
    state.assignedToFilter = null
    return
  }
  state.assignedToFilter = action.payload ?? null
}

// This allows us to invoke `await dispatch(persistTradeBlotterConfigAsyncThunk())` in component code so that
// the component is notified when the API request is complete -- something that isn't possible with listener
// middleware. This also ensure we have access to consistent config state when constructing the request.
export const persistTradeDeskTradeBlotterConfigAsyncThunk = createAsyncThunk(
  tradeDeskTradeBlotterSlice.reducerPath +
    '/persistTradeDeskTradeBlotterConfig',
  async (_data, { dispatch, getState, rejectWithValue }) => {
    const state = (getState() as RootState).tradeDeskTradeBlotter
    try {
      const request: PageConfigRequest = {
        id: Page.TradeDeskTradeBlotter,
        config: state.config,
        version: state.configVersion,
      }
      await dispatch(updatePageConfig.initiate(request))
    } catch (err) {
      const errorMessage =
        // biome-ignore lint/suspicious/noExplicitAny: Bulk suppression as part of biome migration
        (err as { response?: { data?: any } }).response?.data ||
        'An unknown error occurred'
      return rejectWithValue(errorMessage)
    }
  },
)

export const {
  initializeTradeDeskTradeBlotterConfig,

  resetTradeDeskTradeBlotterConfig,
  setOrderStatusGroup,
  setSearchTerm,
  setTradeDeskTradeBlotterConfig,
  setActiveTabQuickFilter,
  setCompletedTabQuickFilter,
  setOrderAssignedToQuickFilter,
} = tradeDeskTradeBlotterSlice.actions

// biome-ignore lint/style/noDefaultExport: Existing default export — prefer named exports for new code
export default tradeDeskTradeBlotterSlice
